import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
} from "lucide-react";
import {
  documentRequestTemplatesApi,
  type CreateDocumentRequestTemplatePayload,
  type DocumentRequestTemplateDto,
  type UpdateDocumentRequestTemplatePayload,
} from "@/api/documentRequestTemplates";
import { ApiError } from "@/api/client";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select, Textarea } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/design-system/lib/utils";
import type { PreApprovalRequestParty } from "@/types/deals";

/**
 * Deal Pipeline spec §3.6 — admin CRUD for the master Pre-Approval document request
 * catalog (the `DocumentTypeRef` table), surfaced as "Document Request
 * Templates". Same UX as the Categories / Roles admin lookups: list +
 * create/edit modal + soft "deactivate" (no hard delete, because in-flight
 * deals reference the code via a restrict FK).
 *
 * Edits, additions, and deactivations affect NEW deals only — a deal's
 * document-request list is spawned from this catalog when it enters
 * Pre-Approval and is never rewritten afterward.
 *
 * "Linked documents" is intentionally not editable here — it is a per-deal
 * concept (evidence attached to a spawned request), not a template field.
 */

const PARTIES: PreApprovalRequestParty[] = [
  "Borrower",
  "Seller",
  "Business",
  "Broker",
  "Multiple",
];

const CODE_REGEX = /^[A-Za-z0-9_]{1,80}$/;

export function DocumentRequestTemplatesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("DocumentRequestTemplates.Manage");
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editTarget, setEditTarget] = useState<DocumentRequestTemplateDto | "new" | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<DocumentRequestTemplateDto | null>(null);

  const list = useQuery({
    queryKey: ["doc-request-templates", true],
    queryFn: () => documentRequestTemplatesApi.list(true),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["doc-request-templates"] });
    // The deal-facing catalog (useDocumentTypes) and the document-request
    // pickers read the live list — refresh them so admin edits propagate
    // immediately.
    void qc.invalidateQueries({ queryKey: ["dealLookups", "documentTypes"] });
  };

  const activeMutation = useMutation({
    mutationFn: (t: DocumentRequestTemplateDto) =>
      t.isActive
        ? documentRequestTemplatesApi.deactivate(t.code)
        : documentRequestTemplatesApi.reactivate(t.code),
    onSuccess: (_data, t) => {
      toast({
        tone: "success",
        message: t.isActive
          ? `Deactivated ${t.displayName}. New deals will no longer include it; existing deals are unchanged.`
          : `Reactivated ${t.displayName}.`,
      });
      setDeactivateTarget(null);
      invalidate();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const filtered = (list.data ?? [])
    .filter((t) => (showInactive ? true : t.isActive))
    .filter((t) => {
      if (!search) return true;
      const needle = search.toLowerCase();
      return (
        t.code.toLowerCase().includes(needle) ||
        t.displayName.toLowerCase().includes(needle) ||
        t.defaultParty.toLowerCase().includes(needle)
      );
    });
  const inactiveCount = (list.data ?? []).filter((t) => !t.isActive).length;

  const columns: Column<DocumentRequestTemplateDto>[] = [
    {
      key: "displayName",
      header: "Title",
      render: (t) => (
        <div className={cn(!t.isActive && "opacity-50")}>
          <div className="font-semibold text-onyx flex items-center gap-space-2">
            <span>{t.displayName}</span>
            {!t.isActive && <Badge status="archived">Inactive</Badge>}
          </div>
          <div className="text-caption text-gray-mid font-mono">{t.code}</div>
          {t.description && (
            <div className="text-caption text-gray-mid font-normal max-w-md truncate">
              {t.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "defaultParty",
      header: "Party",
      render: (t) => (
        <span className={cn("text-small text-onyx", !t.isActive && "opacity-50")}>
          {t.defaultParty}
        </span>
      ),
    },
    {
      key: "defaultDueDays",
      header: "Default due",
      render: (t) => (
        <span className={cn("text-small text-onyx", !t.isActive && "opacity-50")}>
          {t.defaultDueDays} {t.defaultDueDays === 1 ? "day" : "days"}
        </span>
      ),
    },
    {
      key: "defaultRequired",
      header: "Requirement",
      render: (t) => (
        <span
          className={cn(
            "inline-flex items-center px-space-2 py-[2px] rounded-pill text-caption font-display font-semibold border",
            t.defaultRequired
              ? "bg-gold/15 border-gold text-gold-shadow"
              : "bg-white-warm border-border-light text-gray-mid",
            !t.isActive && "opacity-50",
          )}
        >
          {t.defaultRequired ? "Required" : "Optional"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) =>
        canManage ? (
          <RowActions
            template={t}
            pending={activeMutation.isPending}
            onEdit={() => setEditTarget(t)}
            onDeactivate={() => setDeactivateTarget(t)}
            onReactivate={() => activeMutation.mutate(t)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Document Request Templates"
        description="The master catalog of Pre-Approval document requests. Required templates are auto-added to every new deal; optional ones are available to add per deal. Changes here affect new deals only — deals already in progress keep their existing request list. Codes are permanent once issued."
        actions={
          canManage && (
            <Button onClick={() => setEditTarget("new")}>
              <Plus size={16} aria-hidden /> New template
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3 flex-wrap">
        <Input
          type="search"
          placeholder="Search title, code, or party…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          aria-pressed={showInactive}
          className={cn(
            "px-space-2 py-[6px] rounded-full text-caption font-display font-semibold border transition-colors",
            showInactive
              ? "bg-gold/15 border-gold text-gold-shadow"
              : "bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow",
          )}
        >
          Show inactive{inactiveCount > 0 ? ` (${inactiveCount})` : ""}
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage={
          canManage
            ? "No templates match your filter. Click 'New template' to add one."
            : "No document request templates available."
        }
        rowKey={(t) => t.code}
      />

      {editTarget !== null && (
        <TemplateEditModal
          target={editTarget}
          existingCodes={list.data?.map((t) => t.code) ?? []}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidate();
          }}
        />
      )}

      {deactivateTarget && (
        <Modal
          open
          onClose={() => setDeactivateTarget(null)}
          dismissOnBackdropClick={false}
          title={`Deactivate ${deactivateTarget.displayName}?`}
          description="It will no longer be added to new deals. Deals already in progress keep their existing request list, and you can reactivate this template at any time."
          size="sm"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setDeactivateTarget(null)}
                disabled={activeMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => activeMutation.mutate(deactivateTarget)}
                loading={activeMutation.isPending}
              >
                Deactivate
              </Button>
            </>
          }
        >
          <p className="text-small text-gray-mid m-0">
            Code <span className="font-mono text-onyx">{deactivateTarget.code}</span>
          </p>
        </Modal>
      )}
    </>
  );
}

function RowActions({
  template,
  pending,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  template: DocumentRequestTemplateDto;
  pending: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="flex items-center gap-space-1 justify-end" ref={ref}>
      <button
        type="button"
        onClick={onEdit}
        className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
        aria-label={`Edit ${template.code}`}
      >
        <Pencil size={16} aria-hidden />
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-space-1 rounded-md text-gray-mid hover:bg-white-warm hover:text-onyx"
          aria-label={`More actions for ${template.code}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-1 w-56 z-20 bg-white-pure border border-border-light rounded-md shadow-lg overflow-hidden"
          >
            <button
              type="button"
              role="menuitem"
              disabled={pending}
              onClick={() => {
                setOpen(false);
                if (template.isActive) onDeactivate();
                else onReactivate();
              }}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm disabled:opacity-50"
            >
              {template.isActive ? (
                <>
                  <PowerOff size={14} aria-hidden /> Deactivate
                </>
              ) : (
                <>
                  <Power size={14} aria-hidden /> Reactivate
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface TemplateEditModalProps {
  target: DocumentRequestTemplateDto | "new";
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => void;
}

function suggestCode(title: string): string {
  return title
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

function TemplateEditModal({ target, existingCodes, onClose, onSaved }: TemplateEditModalProps) {
  const isNew = target === "new";
  const initial: DocumentRequestTemplateDto | undefined = isNew ? undefined : target;
  const { toast } = useToast();

  const [code, setCode] = useState(initial?.code ?? "");
  const [codeEdited, setCodeEdited] = useState(!isNew);
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [party, setParty] = useState<PreApprovalRequestParty>(initial?.defaultParty ?? "Borrower");
  const [required, setRequired] = useState<boolean>(initial?.defaultRequired ?? true);
  const [dueDays, setDueDays] = useState<string>(
    initial?.defaultDueDays !== undefined ? String(initial.defaultDueDays) : "7",
  );
  const [escalationPath, setEscalationPath] = useState(initial?.defaultEscalationPath ?? "");
  const [notes, setNotes] = useState(initial?.defaultNotes ?? "");
  const [sortOrder, setSortOrder] = useState<string>(
    initial?.sortOrder !== undefined ? String(initial.sortOrder) : "0",
  );
  const [errors, setErrors] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: (body: CreateDocumentRequestTemplatePayload) =>
      documentRequestTemplatesApi.create(body),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Created ${dto.displayName}.` });
      onSaved();
    },
    onError: surfaceError,
  });
  const updateMutation = useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateDocumentRequestTemplatePayload }) =>
      documentRequestTemplatesApi.update(code, body),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Saved ${dto.displayName}.` });
      onSaved();
    },
    onError: surfaceError,
  });

  function surfaceError(e: Error) {
    if (e instanceof ApiError && e.errors.length) setErrors(e.errors);
    else setErrors([e.message]);
  }

  const onTitleChange = (value: string) => {
    setDisplayName(value);
    // Auto-suggest a code from the title until the admin edits the code field.
    if (isNew && !codeEdited) setCode(suggestCode(value));
  };

  const validate = (): string[] => {
    const issues: string[] = [];
    if (isNew) {
      const trimmed = code.trim();
      if (!trimmed) issues.push("Code is required.");
      else if (!CODE_REGEX.test(trimmed))
        issues.push("Code must be 1–80 letters, digits, or underscores (e.g. CREDIT_AUTH).");
      else if (existingCodes.some((c) => c.toLowerCase() === trimmed.toLowerCase()))
        issues.push(`Code '${trimmed}' already exists.`);
    }
    if (!displayName.trim()) issues.push("Title is required.");
    if (displayName.length > 200) issues.push("Title must be 200 characters or fewer.");
    const days = parseInt(dueDays, 10);
    if (!Number.isFinite(days) || days < 0) issues.push("Default due (days) must be zero or greater.");
    return issues;
  };

  const onSubmit = () => {
    setErrors([]);
    const issues = validate();
    if (issues.length) {
      setErrors(issues);
      return;
    }
    const days = parseInt(dueDays, 10);
    const order = parseInt(sortOrder, 10);
    const common = {
      displayName: displayName.trim(),
      description: description.trim() || null,
      defaultParty: party,
      defaultRequired: required,
      defaultEscalationPath: escalationPath.trim() || null,
      defaultNotes: notes.trim() || null,
      sortOrder: Number.isFinite(order) ? order : 0,
    };
    if (isNew) {
      createMutation.mutate({
        ...common,
        code: code.trim(),
        defaultDueDays: Number.isFinite(days) ? days : 7,
      });
    } else if (initial) {
      updateMutation.mutate({
        code: initial.code,
        body: { ...common, defaultDueDays: Number.isFinite(days) ? days : 7 },
      });
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isNew ? "New document request template" : `Edit ${initial?.displayName}`}
      description={
        isNew
          ? "Add a request type to the master catalog. Required types are auto-added to every new deal."
          : "Update this template. Changes affect new deals only — deals already in progress keep their existing request list."
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={pending}>
            {isNew ? "Create template" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField label="Title" required htmlFor="drt-title">
          <Input
            id="drt-title"
            value={displayName}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="e.g. Credit authorization form"
            maxLength={200}
            autoFocus
          />
        </FormField>

        <FormField
          label="Code"
          required={isNew}
          htmlFor="drt-code"
          helperText={
            isNew
              ? "Permanent key. Letters, digits, underscores. Auto-filled from the title — edit if needed."
              : "Codes are permanent — existing deals reference this code. Edit the title to rename."
          }
        >
          {isNew ? (
            <Input
              id="drt-code"
              value={code}
              onChange={(e) => {
                setCodeEdited(true);
                setCode(e.target.value.toUpperCase());
              }}
              placeholder="e.g. CREDIT_AUTH"
              maxLength={80}
              className="font-mono"
            />
          ) : (
            <div
              id="drt-code"
              className="font-mono text-onyx font-semibold px-space-2 py-[6px] bg-white-warm border border-border-light rounded-md inline-block"
            >
              {initial?.code}
            </div>
          )}
        </FormField>

        <FormField
          label="Description"
          htmlFor="drt-desc"
          helperText="Optional — what the counterparty should provide."
        >
          <Textarea
            id="drt-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={2000}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-space-3">
          <FormField label="Party" required htmlFor="drt-party">
            <Select
              id="drt-party"
              value={party}
              onChange={(e) => setParty(e.target.value as PreApprovalRequestParty)}
            >
              {PARTIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Requirement" required htmlFor="drt-required">
            <Select
              id="drt-required"
              value={required ? "required" : "optional"}
              onChange={(e) => setRequired(e.target.value === "required")}
            >
              <option value="required">Required (auto-added to new deals)</option>
              <option value="optional">Optional (add per deal)</option>
            </Select>
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-space-3">
          <FormField
            label="Default due (days)"
            required
            htmlFor="drt-due"
            helperText="Days after the deal enters Pre-Approval."
          >
            <Input
              id="drt-due"
              type="number"
              min={0}
              value={dueDays}
              onChange={(e) => setDueDays(e.target.value)}
              className="max-w-[8rem] font-mono"
            />
          </FormField>

          <FormField
            label="Sort order"
            htmlFor="drt-order"
            helperText="Lower numbers appear first."
          >
            <Input
              id="drt-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="max-w-[8rem] font-mono"
            />
          </FormField>
        </div>

        <FormField
          label="Escalation path"
          htmlFor="drt-escalation"
          helperText="Shown on the spawned request. Leave blank to use the default routing message."
        >
          <Textarea
            id="drt-escalation"
            value={escalationPath}
            onChange={(e) => setEscalationPath(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="e.g. Escalation routes to the Loan Administrator."
          />
        </FormField>

        <FormField
          label="Default notes"
          htmlFor="drt-notes"
          helperText="Optional staff note copied onto each new deal's request."
        >
          <Textarea
            id="drt-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={2000}
          />
        </FormField>

        {errors.length > 0 && (
          <ul className="list-disc pl-space-4 text-caption text-status-error" role="alert">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
